import React, { useState } from "react";
import styled from "styled-components";
import { auth } from "../firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification,
} from "firebase/auth";

// Expand as needed or import a maintained list
const DISPOSABLE_DOMAINS = [
  "mailinator.com",
  "tempmail.com",
  "10minutemail.com",
  "guerrillamail.com",
  "yopmail.com",
];
const RESERVED_DOMAINS = [
  "localhost",
  "127.0.0.1",
  "example.com",
  "test.com",
  "invalid",
  "example",
];
const FAKE_EMAILS = ["test@test.com", "admin@admin.com", "user@user.com"];

function isValidEmail(email) {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return false;
  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) return false;
  if (RESERVED_DOMAINS.includes(domain)) return false;
  if (DISPOSABLE_DOMAINS.some((d) => domain === d || domain.endsWith("." + d)))
    return false;
  if (FAKE_EMAILS.includes(email.toLowerCase())) return false;
  return true;
}

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
`;

const Form = styled.form`
  text-align: center;
  background: #f7f7f7;
  padding: 32px 24px;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
`;

const Input = styled.input`
  margin: 8px;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ddd;
  width: 220px;
`;

const SubmitButton = styled.button`
  margin: 12px;
  padding: 12px 32px;
  border-radius: 24px;
  background: #ffd166;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover:not(:disabled) {
    background-color: #f8b500;
  }

  &:disabled {
    background-color: #f0d8a8;
    cursor: not-allowed;
  }
`;

const GoogleButton = styled.button`
  background: #4285f4;
  color: #fff;
  border: none;
  border-radius: 24px;
  padding: 12px 32px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 18px;
  width: 80%;
  max-width: 260px;
  transition: background-color 0.3s ease;

  &:hover:not(:disabled) {
    background-color: #357ae8;
  }

  &:disabled {
    background-color: #a3c1f7;
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  margin: 24px 0 12px 0;
  color: #aaa;
  font-size: 0.95em;
`;

export default function AuthScreen({ mode = "login", onAuthSuccess = () => {} }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  const isLogin = mode === "login";

  const clearMessages = () => {
    setError("");
    setInfo("");
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    clearMessages();

    if (!isValidEmail(email)) {
      setError("Please enter a valid, non-temporary email address (e.g., you@example.com).");
      return;
    }

    setLoading(true);
    try {
      if (isLogin) {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        if (!userCredential.user.emailVerified) {
          setError("Please verify your email before logging in. Check your inbox for a verification link.");
          await sendEmailVerification(userCredential.user);
          setInfo("Verification email sent again. Please check your inbox.");
          setLoading(false);
          return;
        }
      } else {
        // Sign up mode
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await sendEmailVerification(userCredential.user);
        setInfo("Sign up successful! Please check your email and verify your address before logging in.");
        setLoading(false);
        return; // Don't proceed until verified
      }
      // At this point user is authenticated and email verified
      onAuthSuccess();
    } catch (err) {
      // Map known errors to friendlier messages if you wish, else display err.message
      setError(err.message || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    clearMessages();
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);

      // Google sign-ins do not require email verification, so we can proceed
      onAuthSuccess();
    } catch (err) {
      setError(err.message || "Google sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Form onSubmit={handleAuth} aria-label={`${isLogin ? "Login" : "Sign Up"} Form`}>
        <h2>{isLogin ? "Log In" : "Sign Up"}</h2>
        <Input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value.trim())}
          required
          autoComplete="username"
          aria-label="Email"
          disabled={loading}
        />
        <br />
        <Input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete={isLogin ? "current-password" : "new-password"}
          aria-label="Password"
          disabled={loading}
          minLength={6} // Enforce Firebase minimum password length
        />
        <br />
        <SubmitButton type="submit" disabled={loading}>
          {loading ? (isLogin ? "Logging In..." : "Signing Up...") : isLogin ? "Log In" : "Sign Up"}
        </SubmitButton>
        <Divider>or</Divider>
        <GoogleButton type="button" onClick={handleGoogleSignIn} disabled={loading} aria-label="Continue with Google">
          {loading ? "Please wait..." : "Continue with Google"}
        </GoogleButton>
        {error && (
          <div role="alert" style={{ color: "red", marginTop: 8 }}>
            {error}
          </div>
        )}
        {info && (
          <div role="status" style={{ color: "#217a53", marginTop: 8 }}>
            {info}
          </div>
        )}
      </Form>
    </Wrapper>
  );
}
